import { Box, styled } from "styled-system/jsx";
import { StepHeader, Tag } from "@ttc3k/trekker";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { STEP_ID_PREFIX } from "./scrollToStep";

const StepWrapper = styled(Box, {
  base: {
    transitionDuration: "normal",
    transitionProperty: "height",
    transitionTimingFunction: "ease-in-out",
    display: "flex",
    flexDirection: "column",
    gap: "250",
    scrollMarginTop: "400",
  },
  variants: {
    // A headerless step reads as part of the step above it, so pull it in
    // from the Steps gap (600) to the in-step gap (250).
    attachedToPrevious: {
      true: {
        "&:not(:first-child)": {
          mt: "calc(token(spacing.250) - token(spacing.600))",
        },
      },
    },
  },
});

type CollapsableStepProps = PropsWithChildren & {
  complete: boolean;
  title: string;
  optionalStep?: boolean;
  /** Hide the step header (title + check). Useful when a step has no choice to make. */
  hideHeader?: boolean;
  /** Anchor the checkout checklist scrolls to (see `scrollToStep`). */
  stepId?: string;
};

export const CollapsableStep = ({
  complete,
  title,
  children,
  optionalStep = false,
  hideHeader = false,
  stepId,
}: CollapsableStepProps) => {
  const { t } = useTranslation("booker");
  return (
    <StepWrapper
      attachedToPrevious={hideHeader}
      id={stepId ? `${STEP_ID_PREFIX}${stepId}` : undefined}
      data-step-complete={stepId ? String(complete) : undefined}
    >
      {!hideHeader && (
        <StepHeader
          rightElement={
            optionalStep ? (
              <Tag visual={"highlight"} mode={"light"}>
                {t("OPTIONAL")}
              </Tag>
            ) : undefined
          }
          checked={complete}
        >
          {title}
        </StepHeader>
      )}
      {children}
    </StepWrapper>
  );
};
